import React, { useEffect, useState } from 'react';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import { useIonRouter } from '@ionic/react';
import { loadPersistedWizardState } from '@/modules/share/session_storage';
import { useAppConfig } from '@/modules/config';
import { topicGardenEnabled } from '@/conditional_feature/topic_garden_enabled';
import { MePrFoService } from '@/services/meprfo';
import { decideLegTap } from './useImpliedConfirm';
import { Destinations, routesFor } from './navigation_logic';

/**
 * Where entering `/topics/:topicId/share` lands, read off the persisted
 * session.
 *
 * The session names a destination, never a step or a URL, so the surface now
 * rendering the flow is what turns it into a path: a session left in the wizard
 * resumes at the nearest garden destination when the flag is on, and the other
 * way round when it is off. Which is why this waits for the app config — on a
 * cold load straight into a share link the flag arrives a render or two late,
 * and redirecting before it does would land the sender on the other surface's
 * URL. Nothing is on screen while it waits: this component only ever renders a
 * redirect.
 */
const ShareWizardRedirect: React.FC = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const router = useIonRouter();
  const location = useLocation();
  const { features, isLoaded: isConfigLoaded } = useAppConfig();
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  useEffect(() => {
    if (!isConfigLoaded) return;

    let cancelled = false;
    const routes = routesFor(
      topicGardenEnabled(features) ? 'garden' : 'wizard'
    );
    const rawSearch = router.routeInfo?.search || location.search || '';
    const search = rawSearch.replace(/^\?/, '');
    const params = new URLSearchParams(search);
    const legIdInUrl = params.get('legId');

    loadPersistedWizardState(topicId).then(persisted => {
      if (cancelled) return;
      const { isImpliedConfirm, sendingDestination } = decideLegTap(
        legIdInUrl,
        persisted
      );

      if (legIdInUrl && !isImpliedConfirm) {
        // Leg link tapped by someone other than the sender — count the click
        // then redirect to the action page (not the share flow) so the recipient
        // sees the normal action view. (Today /b/{legId} is handled by
        // LegRedirect; this branch covers any legacy /share?legId= link.)
        MePrFoService.recordLegClickThru(legIdInUrl).catch(() => {});
        params.delete('legId');
        const recipientSearch = params.toString();
        const actionBase = `/topics/${encodeURIComponent(topicId)}`;
        const target = recipientSearch
          ? `${actionBase}?${recipientSearch}`
          : actionBase;
        setRedirectTo(target);
      } else {
        // Sender return, or a fresh start without a legId. When the
        // in-progress leg is the FINAL one in the queue, route the sender
        // straight to the `sent` destination: going via `send` meant
        // ShareConfirmStep's implied-confirm effect would render the final share,
        // then move forward to `sent` — a visible sideways animation on cold
        // load (fresh tab / killed webview). The <Redirect> below is a replace
        // with no Ionic animation, so landing there directly avoids the hop.
        // FinalShareConfirmStep runs the same implied-confirm detection when it
        // sees ?legId.
        const destination = !persisted?.inProgressLegId
          ? Destinations.recipients
          : sendingDestination;
        setRedirectTo(
          routes.pathFor(topicId, routes.nearest(destination), search)
        );
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId, isConfigLoaded, features.topicGardenEnabled]); // intentionally omits search — URL is stable at redirect time

  if (!redirectTo) return null;

  return <Redirect to={redirectTo} />;
};

export default ShareWizardRedirect;
